import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Search, Plus, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { pillStyle } from '@/components/StatusDot';
import { BulkPullReview } from '@/components/BulkPullReview';

const BOROUGHS = ['Manhattan', 'Bronx', 'Brooklyn', 'Queens', 'Staten Island'];
const PROJECT_TYPES = ['New Building', 'Alt-1', 'Alt-2', 'Alt-3', 'DOT Permit', 'FDNY Compliance', 'Violation Resolution'];

// Creatable project-type field: fixed options + previously used custom values + custom entry.
function ProjectTypeField({ value, onChange, knownTypes, testid }) {
  const [customMode, setCustomMode] = useState(false);
  const options = Array.from(new Set([...PROJECT_TYPES, ...(Array.isArray(knownTypes) ? knownTypes : [])])).filter(Boolean);

  if (customMode) {
    return (
      <div className="flex items-center gap-1">
        <Input autoFocus value={value} onChange={e => onChange(e.target.value)} placeholder="Enter custom type…" className="bg-background border-border" data-testid={`${testid}-custom-input`} />
        <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => setCustomMode(false)} data-testid={`${testid}-custom-cancel`}><X className="h-4 w-4" /></Button>
      </div>
    );
  }

  return (
    <Select
      value={value || '__none'}
      onValueChange={v => { if (v === '__custom') { onChange(''); setCustomMode(true); } else onChange(v === '__none' ? '' : v); }}
    >
      <SelectTrigger className="bg-background border-border" data-testid={testid}><SelectValue placeholder="Select type" /></SelectTrigger>
      <SelectContent className="bg-card border-border max-h-64">
        {options.map(t => <SelectItem key={t} value={t} className="text-sm">{t}</SelectItem>)}
        <SelectItem value="__custom" className="text-sm text-primary">+ Custom type…</SelectItem>
      </SelectContent>
    </Select>
  );
}

const EMPTY_FORM = {
  display_name: '', address: '', borough: '', bin: '', block: '', lot: '',
  client_id: '', project_type: '', notes: '',
};

function CountPill({ n, color, testid }) {
  const c = n > 0 ? color : '#031333';
  return (
    <span data-testid={testid} style={{ ...pillStyle(c), fontSize: '12px', padding: '2px 8px', fontFamily: "'DM Mono', monospace" }}>{n}</span>
  );
}

export default function PropertiesList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [violCounts, setViolCounts] = useState({});
  const [permitCounts, setPermitCounts] = useState({});
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Bulk pull review (BIN discover) state.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkData, setBulkData] = useState(null);
  const [bulkPid, setBulkPid] = useState(null);

  const runBulkDiscover = async (pid, form) => {
    if (!(form.bin?.trim() && form.block?.trim() && form.lot?.trim())) return;
    try {
      const r = await api.post(`/properties/${pid}/bulk-discover`);
      if ((r.data?.total || 0) > 0) {
        setBulkData(r.data);
        setBulkPid(pid);
        setBulkOpen(true);
      } else {
        toast.info('No new records found on NYC Open Data for this BIN');
      }
    } catch {
      toast.error('Could not reach NYC Open Data for the bulk pull. Please try again in a moment.');
    }
  };

  const fetchProperties = () =>
    api.get('/properties').then(r => setProperties(Array.isArray(r.data) ? r.data : [])).catch(() => setProperties([]));

  const fetchCounts = () => {
    // Single aggregation endpoint returns violation + permit counts for all properties.
    api.get('/properties/counts').then(r => {
      const pc = r.data?.property_counts || {};
      const vc = {};
      const perm = {};
      Object.entries(pc).forEach(([pid, c]) => {
        vc[pid] = c.open_violations || 0;
        perm[pid] = { active: c.active_permits || 0, exp: c.expiring_permits || 0 };
      });
      setViolCounts(vc);
      setPermitCounts(perm);
    }).catch(() => { setViolCounts({}); setPermitCounts({}); });
  };

  useEffect(() => {
    fetchProperties().finally(() => setLoading(false));
    fetchCounts();
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : [])).catch(() => setClients([]));
  }, []);

  // Legacy deep-link support: /properties?open=<id> now routes to the full profile page.
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) navigate(`/properties/${openId}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const knownTypes = (Array.isArray(properties) ? properties : []).map(p => p.project_type).filter(Boolean);

  const createProperty = async () => {
    if (!addForm.address.trim() || !addForm.borough) return toast.error('Address and Borough are required');
    setSaving(true);
    try {
      const res = await api.post('/properties', { ...addForm, client_id: addForm.client_id || null });
      toast.success('Property created');
      setAddOpen(false);
      const savedForm = addForm;
      setAddForm(EMPTY_FORM);
      fetchProperties();
      if (res.data?.id) runBulkDiscover(res.data.id, savedForm);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error creating property'); }
    setSaving(false);
  };

  const filtered = (Array.isArray(properties) ? properties : []).filter(p => {
    const q = search.toLowerCase();
    return (p.display_name || '').toLowerCase().includes(q) ||
      (p.address || '').toLowerCase().includes(q) ||
      (p.borough || '').toLowerCase().includes(q) ||
      (p.bin || '').toLowerCase().includes(q) ||
      (p.project_type || '').toLowerCase().includes(q) ||
      (p.client_name || '').toLowerCase().includes(q);
  });

  const renderFormFields = (form, setForm, idPrefix) => (
    <div className="space-y-3">
      <div>
        <Label className="text-xs text-muted-foreground">Display Name</Label>
        <Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="e.g. 100 Broadway Tower" className="bg-background border-border" data-testid={`${idPrefix}-display-name`} />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Address *</Label>
        <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="e.g. 100 Broadway" className="bg-background border-border" data-testid={`${idPrefix}-address`} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-muted-foreground">Borough *</Label>
          <Select value={form.borough || '__none'} onValueChange={v => setForm({ ...form, borough: v === '__none' ? '' : v })}>
            <SelectTrigger className="bg-background border-border" data-testid={`${idPrefix}-borough`}><SelectValue placeholder="Select borough" /></SelectTrigger>
            <SelectContent className="bg-card border-border">{BOROUGHS.map(b => <SelectItem key={b} value={b} className="text-sm">{b}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Type</Label>
          <ProjectTypeField value={form.project_type} onChange={v => setForm({ ...form, project_type: v })} knownTypes={knownTypes} testid={`${idPrefix}-type`} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><Label className="text-xs text-muted-foreground">BIN</Label><Input value={form.bin} onChange={e => setForm({ ...form, bin: e.target.value })} className="bg-background border-border" data-testid={`${idPrefix}-bin`} /></div>
        <div><Label className="text-xs text-muted-foreground">Block</Label><Input value={form.block} onChange={e => setForm({ ...form, block: e.target.value })} className="bg-background border-border" data-testid={`${idPrefix}-block`} /></div>
        <div><Label className="text-xs text-muted-foreground">Lot</Label><Input value={form.lot} onChange={e => setForm({ ...form, lot: e.target.value })} className="bg-background border-border" data-testid={`${idPrefix}-lot`} /></div>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Client</Label>
        <Select value={form.client_id || '__none'} onValueChange={v => setForm({ ...form, client_id: v === '__none' ? '' : v })}>
          <SelectTrigger className="bg-background border-border" data-testid={`${idPrefix}-client`}><SelectValue placeholder="No client" /></SelectTrigger>
          <SelectContent className="bg-card border-border max-h-64">
            <SelectItem value="__none" className="text-sm text-muted-foreground">No client</SelectItem>
            {(Array.isArray(clients) ? clients : []).map(c => <SelectItem key={c.id} value={c.id} className="text-sm">{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">Notes</Label>
        <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="bg-background border-border" data-testid={`${idPrefix}-notes`} />
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="properties-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground" data-testid="properties-count">{filtered.length} {filtered.length === 1 ? 'property' : 'properties'}</p>
        <Button className="bg-primary text-primary-foreground" onClick={() => { setAddForm(EMPTY_FORM); setAddOpen(true); }} data-testid="add-property-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Property
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search properties..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="properties-search" />
      </div>

      <div className="bg-transparent">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Display Name</TableHead>
              <TableHead className="text-xs text-muted-foreground">Address</TableHead>
              <TableHead className="text-xs text-muted-foreground">Borough</TableHead>
              <TableHead className="text-xs text-muted-foreground">BIN</TableHead>
              <TableHead className="text-xs text-muted-foreground">Client</TableHead>
              <TableHead className="text-xs text-muted-foreground text-center">Violations</TableHead>
              <TableHead className="text-xs text-muted-foreground text-center">Permits</TableHead>
              <TableHead className="text-xs text-muted-foreground text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(p => {
              const openV = violCounts[p.id] || 0;
              const perm = permitCounts[p.id] || { active: 0, exp: 0 };
              return (
                <TableRow key={p.id} className="group border-border hover:bg-secondary/30 cursor-pointer" onClick={() => navigate(`/properties/${p.id}`)} data-testid={`property-row-${p.id}`}>
                  <TableCell className="font-medium text-sm" style={{ color: '#010A21' }}>{p.display_name || '\u00A0'}</TableCell>
                  <TableCell className="text-sm">{p.address}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.borough}</TableCell>
                  <TableCell className="text-sm font-mono text-muted-foreground">{p.bin || '\u00A0'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.client_name || '\u00A0'}</TableCell>
                  <TableCell className="text-center"><CountPill n={openV} color="#E5441A" testid={`property-violations-count-${p.id}`} /></TableCell>
                  <TableCell className="text-center"><CountPill n={perm.active} color={perm.exp > 0 ? '#D97706' : '#1B4A76'} testid={`property-permits-count-${p.id}`} /></TableCell>
                  <TableCell className="text-right"><ChevronRight className="h-4 w-4 text-muted-foreground inline opacity-0 group-hover:opacity-100 transition-opacity" /></TableCell>
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">No properties found. Click "Add Property" to create one.</TableCell></TableRow>
            )}
            {loading && (
              <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">Loading properties...</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Property Sheet */}
      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent className="sm:max-w-md bg-card border-border overflow-y-auto" data-testid="add-property-sheet">
          <SheetHeader>
            <SheetTitle>Add Property</SheetTitle>
            <SheetDescription>Create a new property record.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {renderFormFields(addForm, setAddForm, 'add-property')}
            <div className="flex gap-2 mt-5">
              <Button onClick={createProperty} disabled={saving} className="bg-primary text-primary-foreground" data-testid="submit-add-property">
                {saving ? 'Saving...' : 'Create Property'}
              </Button>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      {/* Bulk pull review (BIN discover) */}
      <BulkPullReview
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        propertyId={bulkPid}
        data={bulkData}
        onAdded={() => fetchCounts()}
      />
    </div>
  );
}
